import {
  and,
  ControlProps,
  isOneOfEnumControl,
  JsonSchema,
  optionIs,
  OwnPropsOfEnum,
  rankWith,
  RankedTester,
} from "@jsonforms/core";
import { withJsonFormsOneOfEnumProps } from "@jsonforms/react";
import { RadioGroup } from "@headlessui/react";
import { useEffect, useMemo } from "react";

import { Badge } from "@/components/elements/Badge/Badge";
import { RadioButtonCard } from "@/components/elements/ButtonCard/ButtonCard";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import cn from "@/helpers/classNames";

type RadioOneOfEnumControlProps = ControlProps & OwnPropsOfEnum;

// `oneOf` schema entries aren't typed by the base `JsonSchema`, but the
// backend can attach a per-option `description`, `recommended` and
// `default` flag, mirroring the FormFactory `choice` field type's
// `ChoiceOption`.
type OneOfOptionSchema = JsonSchema & {
  const?: unknown;
  description?: string;
  recommended?: boolean;
  default?: unknown;
};

function RadioOneOfEnumControlComponent({
  data,
  handleChange,
  path,
  label,
  description,
  schema,
  options = [],
  visible,
  enabled,
  required,
}: RadioOneOfEnumControlProps) {
  const optionSchemas = useMemo(
    () => (schema?.oneOf ?? []) as OneOfOptionSchema[],
    [schema?.oneOf],
  );

  const items = useMemo(() => {
    return options.map((option) => {
      const optionSchema = optionSchemas.find(
        (entry) => entry.const === option.value,
      );
      return {
        ...option,
        description: optionSchema?.description,
        recommended: Boolean(optionSchema?.recommended),
      };
    });
  }, [options, optionSchemas]);

  // The schema's `default` may either be the standard top-level JSON Schema
  // `default` keyword, or - matching the `recommended` flag convention used
  // for these `oneOf` options - a `default: true` flag set on the specific
  // option's schema entry.
  const schemaDefault = schema?.default;
  const defaultValue = useMemo(() => {
    if (schemaDefault !== undefined) return schemaDefault;
    return optionSchemas.find((entry) => entry.default === true)?.const;
  }, [schemaDefault, optionSchemas]);

  // JsonForms doesn't apply JSON Schema `default` values to `data` on its
  // own - unlike array items' `createDefaultValue`, a plain control's
  // initial value is whatever the caller passed in. Without this, a
  // `default` declared on the schema would never get selected or written
  // back into the form data.
  useEffect(() => {
    if (data === undefined && defaultValue !== undefined) {
      // Deferred: dispatching synchronously during this effect can race
      // ahead of JsonForms' own context-subscription effect (which hasn't
      // registered its listener yet on first mount), silently dropping the
      // update. Yielding a tick ensures the subscription is in place first.
      const timeoutId = setTimeout(() => {
        handleChange(path, defaultValue);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, path, defaultValue]);

  if (!visible) return null;

  return (
    <div>
      <FieldLabel htmlFor={path} required={required}>
        {label}
      </FieldLabel>
      {!!description && <InputDescription>{description}</InputDescription>}
      <RadioGroup
        value={data ?? null}
        onChange={(value) => handleChange(path, value)}
        className="mt-2 grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2"
      >
        {items.map((item) => (
          <RadioGroup.Option
            key={String(item.value)}
            value={item.value}
            disabled={!enabled}
            className="flex"
          >
            {({ checked }) => (
              <RadioButtonCard
                isChecked={checked}
                disabled={!enabled}
                className="w-full flex-1 items-start gap-3 p-4 text-left whitespace-normal dark:bg-gray-900/20"
              >
                <span
                  aria-hidden
                  className={cn(
                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                    checked
                      ? "border-blue-600 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-600",
                  )}
                >
                  {checked && (
                    <span className="size-2 rounded-full bg-blue-500 dark:bg-blue-400" />
                  )}
                </span>
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{item.label}</span>
                    {!!item.recommended && (
                      <Badge variant="info">Recommended</Badge>
                    )}
                  </span>
                  {!!item.description && (
                    <span className="text-xs text-muted-foreground">
                      {item.description}
                    </span>
                  )}
                </span>
              </RadioButtonCard>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </div>
  );
}

export const RadioOneOfEnumControl = withJsonFormsOneOfEnumProps(
  RadioOneOfEnumControlComponent,
);

// Ranked higher than the default oneOfEnumControlTester (5) so a `oneOf`
// control with `"options": { "format": "radio" }` in its UI schema renders
// as radio cards instead of the default select dropdown.
export const radioOneOfEnumControlTester: RankedTester = rankWith(
  6,
  and(isOneOfEnumControl, optionIs("format", "radio")),
);
